import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../../components/Badge';
import DataTable, { Column } from '../../components/DataTable';
import Modal from '../../components/Modal';
import FormField from '../../components/FormField';
import { useCredentials, useRevocationHistory, useMyDIDs } from '../../api/hooks';
import { apiFetch } from '../../api/client';
import { formatRevocationReason } from '../../lib/revocationReasons';

interface Credential {
  id: string;
  schema: string;
  holder: string;
  issuedAt: string;
  status: 'active' | 'revoked' | 'suspended';
  revokedAt?: string;
  reason?: string;
}

const reasonCodes = ['01', '02', '03', '04', '05', '06', '07', '08', '09'];

export default function RevocationManagement() {
  // Credentials are filtered by the issuer's DID. The API has no `current`
  // alias, so resolve the caller's own DID first (same pattern as
  // CredentialIssuance); until it is known the query is skipped rather than
  // sent with a literal that matches nothing.
  const { data: myDIDs } = useMyDIDs();
  const issuerDid = (myDIDs ?? []).find((d) => d.status === 'active')?.did ?? (myDIDs ?? [])[0]?.did;
  const { data: credentials, loading: credLoading, error: credError, refetch: refetchCreds } =
    useCredentials(issuerDid ? `issuer=${encodeURIComponent(issuerDid)}` : null);
  const { data: revocationHistory, loading: histLoading, refetch: refetchHist } = useRevocationHistory();
  const { t } = useTranslation(['issuer', 'common']);

  const [search, setSearch] = useState('');
  const [showRevoke, setShowRevoke] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<Credential | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBatchRevoke, setShowBatchRevoke] = useState(false);
  const [batchReason, setBatchReason] = useState('');
  const [revokeError, setRevokeError] = useState('');

  // The API returns W3C credentials (credentialSchema / credentialSubject.id),
  // not the flat {schema, holder, status} the table renders. Casting straight
  // across left Schema/Holder/Issued blank and every status undefined, so the
  // summary cards counted 0 active and 0 revoked.
  const allCredentials: Credential[] = (credentials ?? []).map((raw) => {
    const c = raw as unknown as {
      id: string;
      revoked?: boolean;
      issuanceDate?: string;
      revokedAt?: string;
      revocationReason?: string;
      credentialSchema?: { id?: string };
      credentialSubject?: { id?: string };
      type?: string[];
    };
    return {
      id: c.id,
      schema: (c.type ?? []).find((t) => t !== 'VerifiableCredential') ?? c.credentialSchema?.id ?? '—',
      holder: c.credentialSubject?.id ?? '—',
      issuedAt: c.issuanceDate ?? '',
      status: c.revoked ? 'revoked' : 'active',
      revokedAt: c.revokedAt,
      reason: c.revocationReason,
    } as Credential;
  });
  const filtered = allCredentials.filter(
    (c) =>
      c.id.toLowerCase().includes(search.toLowerCase()) ||
      c.holder.toLowerCase().includes(search.toLowerCase()) ||
      c.schema.toLowerCase().includes(search.toLowerCase())
  );

  const handleRevoke = async (credentialIds: string[], reason: string): Promise<boolean> => {
    setRevoking(true);
    setRevokeError('');
    try {
      // Field names must match the API: a single revoke takes
      // {credentialId, issuerDid, reason}; the batch endpoint takes
      // {credentialIds, issuerDid, reason}. Sending {credentialIds, reasonCode}
      // to the single endpoint was rejected, so revoking appeared to do nothing.
      if (credentialIds.length === 1) {
        await apiFetch('/credentials/revoke', {
          method: 'POST',
          body: JSON.stringify({ credentialId: credentialIds[0], issuerDid, reason }),
        });
      } else {
        await apiFetch('/credentials/batch-revoke', {
          method: 'POST',
          body: JSON.stringify({ credentialIds, issuerDid, reason }),
        });
      }
      refetchCreds();
      refetchHist();
      return true;
    } catch (e) {
      // Surface the failure — this used to swallow errors, so a rejected
      // revoke looked identical to a successful one.
      setRevokeError(e instanceof Error ? e.message : 'Failed to revoke credential');
      return false;
    } finally {
      setRevoking(false);
    }
  };

  const columns: Column<Credential>[] = [
    {
      key: 'id',
      header: t('revocation.table.id'),
      render: (row) => <span className="font-mono text-xs font-bold text-primary">{row.id}</span>,
    },
    {
      key: 'schema',
      header: t('revocation.table.schema'),
      render: (row) => <span className="text-sm font-bold text-text-main">{row.schema}</span>,
    },
    {
      key: 'holder',
      header: t('revocation.table.holder'),
      render: (row) => (
        <span className="inline-block rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-text-main">
          {row.holder}
        </span>
      ),
    },
    {
      key: 'issuedAt',
      header: t('revocation.table.issued'),
      render: (row) => <span className="text-xs text-text-muted">{row.issuedAt}</span>,
      sortable: true,
      sortValue: (row) => row.issuedAt,
    },
    {
      key: 'status',
      header: t('revocation.table.status'),
      render: (row) => (
        <Badge
          variant={row.status === 'active' ? 'success' : row.status === 'revoked' ? 'error' : 'warning'}
        >
          {row.status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) =>
        row.status === 'active' ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setRevokeTarget(row);
              setShowRevoke(true);
            }}
            className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-[10px] font-bold text-red-700 transition-colors hover:bg-red-100"
          >
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>block</span>
            {t('revocation.table.revoke')}
          </button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('revocation.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('revocation.subtitle')}</p>
        </div>
        {selectedIds.size > 0 && (
          <button className="btn-primary bg-red-500 shadow-red-200 hover:bg-red-600" onClick={() => setShowBatchRevoke(true)}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>block</span>
            {t('revocation.batchRevoke', { count: selectedIds.size })}
          </button>
        )}
      </div>

      {credLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : credError ? (
        <div className="text-center py-20 text-red-500">{t('common:errors.loadCredentialsFailed')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
            {[
              { label: t('revocation.stats.total'), value: String(allCredentials.length), icon: 'description' },
              { label: t('revocation.stats.active'), value: String(allCredentials.filter((c) => c.status === 'active').length), icon: 'check_circle' },
              { label: t('revocation.stats.revoked'), value: String(allCredentials.filter((c) => c.status === 'revoked').length), icon: 'block' },
              { label: t('revocation.stats.suspended'), value: String(allCredentials.filter((c) => c.status === 'suspended').length), icon: 'pause_circle' },
            ].map((stat) => (
              <div key={stat.label} className="stat-card">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 16 }}>{stat.icon}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">{stat.label}</span>
                </div>
                <span className="mt-2 block text-3xl font-bold text-text-main">{stat.value}</span>
              </div>
            ))}
          </div>

          <div className="relative max-w-md">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-text-faint" style={{ fontSize: 20 }}>search</span>
            <input
              className="h-10 w-full rounded-lg border border-border-subtle bg-slate-50 pl-10 pr-4 text-sm text-text-main placeholder:text-text-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              placeholder={t('revocation.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <DataTable
            columns={columns}
            data={filtered}
            getId={(row) => row.id}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            pageSize={10}
          />

          <div>
            <div className="mb-4 flex items-center gap-2">
              <span className="material-symbols-outlined text-text-muted" style={{ fontSize: 18 }}>history</span>
              <h2 className="text-sm font-extrabold uppercase tracking-widest text-text-main">{t('revocation.history')}</h2>
            </div>
            {histLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border-subtle bg-white shadow-sm">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-subtle bg-slate-50/50">
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('revocation.historyTable.timestamp')}</th>
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('revocation.historyTable.credential')}</th>
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('revocation.historyTable.action')}</th>
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('revocation.historyTable.reason')}</th>
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('revocation.historyTable.actor')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(revocationHistory ?? []).map((h) => (
                      <tr key={h.id} className="border-b border-border-subtle transition-colors hover:bg-slate-50/60">
                        <td className="px-6 py-3 font-mono text-xs text-text-muted">{h.timestamp}</td>
                        <td className="px-6 py-3 font-mono text-xs font-bold text-primary">{h.credentialId}</td>
                        <td className="px-6 py-3">
                          <Badge variant={h.action === 'Revoked' ? 'error' : 'warning'}>{h.action}</Badge>
                        </td>
                        <td className="px-6 py-3 text-xs font-bold text-text-main">
                          {/* Stored as a reason code ("01"); show the label. */}
                          {h.reason
                            ? formatRevocationReason(h.reason)
                            : <span className="font-normal text-text-faint">—</span>}
                        </td>
                        <td className="px-6 py-3">
                          {h.actor ? (
                            <span className="inline-block max-w-[220px] truncate rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-text-main" title={h.actor}>
                              {h.actor}
                            </span>
                          ) : (
                            <span className="text-xs text-text-faint">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <Modal open={showRevoke} onClose={() => { setShowRevoke(false); setRevokeTarget(null); setRevokeReason(''); }} title={t('revocation.modal.revokeTitle')}>
        {revokeTarget && (
          <div className="space-y-5">
            <div className="rounded-lg border border-border-subtle bg-slate-50 p-4">
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('revocation.modal.credentialId')}</span>
                  <span className="font-mono font-bold text-primary">{revokeTarget.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('revocation.modal.schema')}</span>
                  <span className="font-bold text-text-main">{revokeTarget.schema}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-text-faint">{t('revocation.modal.holder')}</span>
                  <span className="font-mono font-bold text-text-main">{revokeTarget.holder}</span>
                </div>
              </div>
            </div>
            <FormField
              as="select"
              label={t('revocation.modal.reasonLabel')}
              value={revokeReason}
              onChange={(e) => setRevokeReason((e.target as HTMLSelectElement).value)}
            >
              <option value="">{t('revocation.modal.selectReason')}</option>
              {reasonCodes.map((code) => (
                <option key={code} value={code}>{code} - {t(`revocation.reasons.${code}`)}</option>
              ))}
            </FormField>
            {revokeError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{revokeError}</div>
            )}
            <div className="flex justify-end gap-3">
              <button className="btn-secondary" onClick={() => { setShowRevoke(false); setRevokeTarget(null); setRevokeError(''); }}>{t('common:actions.cancel')}</button>
              <button
                className="btn-primary bg-red-500 shadow-red-200 hover:bg-red-600"
                disabled={!revokeReason || revoking}
                onClick={async () => {
                  const ok = await handleRevoke([revokeTarget.id], revokeReason);
                  // Keep the dialog open on failure so the reason is visible.
                  if (ok) {
                    setShowRevoke(false);
                    setRevokeTarget(null);
                    setRevokeReason('');
                  }
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>block</span>
                {revoking ? t('revocation.modal.revoking') : t('revocation.modal.confirm')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={showBatchRevoke} onClose={() => { setShowBatchRevoke(false); setBatchReason(''); }} title={t('revocation.modal.batchTitle', { count: selectedIds.size })}>
        <div className="space-y-5">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-2">
              <span className="material-symbols-outlined text-amber-600" style={{ fontSize: 20 }}>warning</span>
              <div>
                <p className="text-sm font-bold text-amber-800">{t('revocation.modal.warningTitle')}</p>
                <p className="mt-1 text-xs text-amber-700">
                  {t('revocation.modal.warningSubtitle', { count: selectedIds.size })}
                </p>
              </div>
            </div>
          </div>
          <FormField
            as="select"
            label={t('revocation.modal.batchReasonLabel')}
            value={batchReason}
            onChange={(e) => setBatchReason((e.target as HTMLSelectElement).value)}
          >
            <option value="">{t('revocation.modal.selectReason')}</option>
            {reasonCodes.map((code) => (
              <option key={code} value={code}>{code} - {t(`revocation.reasons.${code}`)}</option>
            ))}
          </FormField>
          {revokeError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{revokeError}</div>
          )}
          <div className="flex justify-end gap-3">
            <button className="btn-secondary" onClick={() => { setShowBatchRevoke(false); setRevokeError(''); }}>{t('common:actions.cancel')}</button>
            <button
              className="btn-primary bg-red-500 shadow-red-200 hover:bg-red-600"
              disabled={!batchReason || revoking}
              onClick={async () => {
                const ok = await handleRevoke(Array.from(selectedIds), batchReason);
                if (ok) {
                  setShowBatchRevoke(false);
                  setSelectedIds(new Set());
                  setBatchReason('');
                }
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>block</span>
              {revoking ? t('revocation.modal.revoking') : t('revocation.modal.batchConfirm', { count: selectedIds.size })}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
